import { Button, ButtonProps } from 'components/Button';
import { useFormikContext } from 'formik';
import { debounce } from 'lodash-es';
import React, { ButtonHTMLAttributes, MouseEvent } from 'react';
import { formErrorKey } from 'services/forms/forms.config';

export type SubmitButtonProps = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'type'
> &
  ButtonProps & {
    // enableDisabledOnPristine: disables the submit button if the current form
    // value is equal to the initialValues.
    enableDisabledOnPristine?: boolean;
    shouldValidateForm?: boolean;
  };

const SubmitButton = React.memo(function SubmitButton({
  onClick,
  disabled: disabledFromProps,
  enableDisabledOnPristine = false,
  shouldValidateForm = true,
  ...otherProps
}: SubmitButtonProps) {
  const { isSubmitting, errors, validateForm, values, dirty } =
    useFormikContext<any>();
  const pristine = !dirty;

  const amountOfErrors = Object.keys(errors).length;
  const debouncedValidation = React.useMemo(
    () => debounce(validateForm, 400, { leading: true }),
    [validateForm],
  );

  // Triggers debounced validation on values change.
  React.useEffect(() => {
    if (shouldValidateForm) {
      debouncedValidation();
    }
  }, [debouncedValidation, shouldValidateForm, values]);

  // Prevents memory leaks:
  // we should not trigger the validation if the component gets unmounted.
  React.useEffect(
    () => () => debouncedValidation.cancel(),
    [debouncedValidation],
  );

  // We want to allow submission if the user has a 'formErrorKey' error
  // because it might be due to a network error or a random server hiccup.
  const isValid =
    amountOfErrors === 0 ||
    (amountOfErrors === 1 && typeof errors[formErrorKey] === 'string');

  const disabledIfPristine = enableDisabledOnPristine ? pristine : false;

  // Use 'props.disabled' to override formik logic.
  const disabled =
    disabledFromProps || isSubmitting || !isValid || disabledIfPristine;

  const handleClick = React.useCallback(
    (domEvent: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>) => {
      if (isSubmitting || disabled) {
        domEvent.preventDefault();
      }

      // In ButtonBase we have set  '&[disabled] { pointer-events: none; }'
      // but just to be more resilient we add a check here.
      if (!disabled && typeof onClick === 'function') {
        onClick(domEvent);
      }
    },
    [disabled, isSubmitting, onClick],
  );

  return (
    <Button
      {...otherProps}
      onClick={handleClick}
      disabled={disabled}
      type="submit"
    />
  );
});

export { SubmitButton };
